/*
 * Copyright (c) 2022. Nedaware SL
 */

import {FilterInterface, InvalidFilterProperty, InvalidFilterValue} from "../../api/filter-interface";
import {InternalPropertyMap} from "../basic-entity-interface/mapping-internal";
import {TypeStr} from "../property-type/type-str";
import {FilterGroup} from "../basic-entity-interface/mapping-external";

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */

/**
 * The date filter allows to filter dates. It accepts only values which, after casting (if possible)
 * for the property, they evaluate as strings.
 */
abstract class DateFilterBase implements FilterInterface {
    protected _dataWithComparisonToParams(comparison: "after" | "before" | "between", strict: boolean, property: InternalPropertyMap | null,
                                          data: any[] | any): { [p: string]: string | string[] } {
        if (property == null) {
            throw new InvalidFilterProperty(property, "the property cannot be null");
        }

        if (![TypeStr.Date, TypeStr.DateTime, TypeStr.Time].includes(property.type.toString())) {
            throw new InvalidFilterProperty(
                property, 'date filters accept exclusively properties of type Date, DateTime and Time. Sent: ' + property.type.toString());
        }

        const method = (strict ? 'strictly_' : '') + comparison;
        const param = `${property.paramName}[${method}]`;

        if (comparison === "between") {
            if (!Array.isArray(data) || data.length !== 2) {
                throw new InvalidFilterValue(data, "for between range filter only an array of two elements is accepted.");
            }
            return {[param]: `${data[0]}...${data[1]}`};
        } else {

            if (property.caster) {
                data = property.caster.fromModel(data);
            }

            if (typeof data !== "string") {
                throw new InvalidFilterValue(
                    data, 'the value could not be casted to string, please check the property has a proper caster.');
            }

            return {[param]: data};
        }
    }

    abstract dataToParams(property: InternalPropertyMap, data: any[] | any): { [p: string]: string | string[] };
}

/**
 * The "before" date-filter allows to filter for a date before the specified one
 */
export class BeforeDateFilter extends DateFilterBase {
    dataToParams(property: InternalPropertyMap | null, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataWithComparisonToParams("before", false, property, data);
    }
}

/**
 * The "before" date-filter allows to filter for a date before the specified one
 */
export class AfterDateFilter extends DateFilterBase {
    dataToParams(property: InternalPropertyMap | null, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataWithComparisonToParams("after", false, property, data);
    }
}

/**
 * The "before" date-filter allows to filter for a date before the specified one
 */
export class StrictlyBeforeDateFilter extends DateFilterBase {
    dataToParams(property: InternalPropertyMap | null, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataWithComparisonToParams("before", true, property, data);
    }
}

/**
 * The "before" date-filter allows to filter for a date before the specified one
 */
export class StrictlyAfterDateFilter extends DateFilterBase {
    dataToParams(property: InternalPropertyMap | null, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataWithComparisonToParams("after", true, property, data);
    }
}

/**
 * The "before" date-filter allows to filter for a date before the specified one
 */
export class BetweenDateFilter extends DateFilterBase {
    dataToParams(property: InternalPropertyMap | null, data: any[] | any): { [p: string]: string | string[] } {
        return this._dataWithComparisonToParams("between", true, property, data);
    }
}

/**
 * Date filters allow to filter dates
 * @see BeforeDateFilter
 * @see AfterDateFilter
 * @see StrictlyBeforeDateFilter
 * @see StrictlyAfterDateFilter
 * @see BetweenDateFilter
 */
export const DateFilters: FilterGroup = [
    BeforeDateFilter, AfterDateFilter, StrictlyBeforeDateFilter, StrictlyAfterDateFilter, BetweenDateFilter
];
